import { fetchEventSource } from "@microsoft/fetch-event-source";
import * as Sentry from "@sentry/react";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import type {
  HydratedImage,
  RegenerateWithAttributesRequest,
  RegenerateWithPromptRequest,
  SelectionCreateDesignRequest,
  SelectionReplaceAssetRequest,
} from "../generated/api/types.gen";
import { hrefReturningHere } from "./auth-redirect";
import { SUBSCRIPTION_REQUIRED_DETAIL } from "./errors";
import type { RefinementStreamEvent } from "./frontend-types";

// Determine the base URL
const getBaseURL = () => {
  // Check if we have an environment variable
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Default to backend URL for development
  // EventSource doesn't work well with Vite proxy, so we need the full URL
  if (import.meta.env.DEV) {
    return "http://localhost:8080";
  }

  return "";
};

export const BASE_URL = getBaseURL();

// Extend AxiosRequestConfig to include retry flag and token
declare module "axios" {
  export interface AxiosRequestConfig {
    __isRetry?: boolean;
    __skipAuth?: boolean;
  }
}

// Create axios instance with default config. No default Content-Type:
// axios infers it per request (application/json for plain objects,
// multipart/form-data with boundary for FormData). A global JSON default
// would make axios JSON-stringify FormData bodies from the generated SDK.
export const apiClient = axios.create({
  baseURL: BASE_URL,
});

// Token getter function - will be set by ClerkAuthProvider
let getTokenFn: (() => Promise<string | null>) | null = null;

export function setTokenGetter(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

// Request interceptor - add Clerk JWT token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip auth for specific requests
    if (config.__skipAuth) {
      return config;
    }

    // Get token from Clerk
    if (getTokenFn) {
      try {
        const token = await getTokenFn();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Error getting auth token:", error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors and redirect on auth failure
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // TanStack Query aborts stale requests on unmount/navigation and axios
    // surfaces that as CanceledError (no response). A cancel is not a
    // failure, so skip Sentry and just propagate.
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      return Promise.reject(error);
    }

    // Log non-auth errors to Sentry
    if (error.response?.status !== 401 && error.response?.status !== 403) {
      Sentry.captureException(error, {
        tags: {
          errorType: "api-error",
          endpoint: error.config?.url,
          method: error.config?.method,
          statusCode: error.response?.status,
        },
        contexts: {
          response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
          },
        },
      });
    }

    if (error.response?.status === 401) {
      // Redirect to sign-in page
      if (!window.location.pathname.startsWith("/sign-in")) {
        window.location.href = hrefReturningHere("/sign-in");
      }
    } else if (error.response?.status === 403) {
      const detail = (error.response?.data as { detail?: string })?.detail;
      // Only the paid-plan gate sends the user to pricing. An admin-gate 403
      // is not about billing, and an iteration-limit 403 resolves next month —
      // that one surfaces as the out-of-iterations overlay on the page the
      // user is already on, so it must not navigate.
      if (detail === SUBSCRIPTION_REQUIRED_DETAIL) {
        if (!window.location.pathname.startsWith("/pricing")) {
          window.location.href = hrefReturningHere("/pricing");
        }
      }
    }

    return Promise.reject(error);
  },
);

// Helper to get auth token
const getAuthToken = async () => {
  if (getTokenFn) {
    try {
      return await getTokenFn();
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
  }
  return null;
};

interface StreamRequestOptions {
  url: string;
  method: "POST";
  body: Record<string, unknown> | FormData;
  headers?: Record<string, string>;
  requestId?: string;
}

function buildStreamHeaders(
  options: StreamRequestOptions,
  token: string | null,
  requestId: string,
): Record<string, string> {
  return {
    ...(options.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "X-Request-ID": requestId,
    ...options.headers,
  };
}

/**
 * Build the error for a stream whose response was not ok, surfacing the
 * server's `detail` (e.g. "Image too large. Maximum size is 20MB.") instead of
 * a bare status code, and report it to Sentry the way the axios interceptor
 * reports REST failures. The body may not be JSON — a proxy error page, say —
 * in which case the status line is all there is to show.
 */
export async function toStreamOpenError(
  response: Response,
  url: string,
): Promise<Error & { status: number }> {
  const data: unknown = await response.json().catch(() => null);
  const detail =
    typeof data === "object" && data !== null && "detail" in data
      ? data.detail
      : undefined;
  const message =
    typeof detail === "string"
      ? detail
      : `Failed to connect: ${response.status} ${response.statusText}`;
  // Carry the status so a 403 (e.g. a spent iteration allowance) can be told
  // apart from a network failure downstream.
  const error = Object.assign(new Error(message), { status: response.status });

  if (response.status !== 401 && response.status !== 403) {
    Sentry.captureException(error, {
      // Every stream error is thrown from this one spot, so without a
      // fingerprint Sentry would fold all of them into a single issue.
      fingerprint: ["stream-open-error", String(response.status)],
      tags: {
        errorType: "stream-error",
        endpoint: url,
        statusCode: response.status,
      },
      contexts: {
        response: { status: response.status, data },
      },
    });
  }

  return error;
}

function processStreamMessage<T>(
  msg: { data: string },
  push: (item: T) => void,
  setDone: () => void,
  setError: (err: Error) => void,
) {
  if (msg.data === "[DONE]") {
    setDone();
    return;
  }
  if (!msg.data || msg.data.trim() === "") return;
  try {
    const data = JSON.parse(msg.data);
    if (data.error) throw new Error(data.error);
    push(data);
  } catch (err) {
    setError(err instanceof Error ? err : new Error(String(err)));
    throw err;
  }
}

function createStreamQueue<T>() {
  let error: Error | null = null;
  const queue: T[] = [];
  let resolveQueue: (() => void) | null = null;
  let done = false;

  const signal = () => {
    if (resolveQueue) {
      resolveQueue();
      resolveQueue = null;
    }
  };
  const push = (item: T) => {
    queue.push(item);
    signal();
  };
  const setError = (err: Error) => {
    error = err;
    signal();
  };
  const setDone = () => {
    done = true;
    signal();
  };

  return {
    queue,
    push,
    setError,
    setDone,
    getError: () => error,
    isDone: () => done,
    wait: () =>
      new Promise<void>((resolve) => {
        resolveQueue = resolve;
      }),
  };
}

// Helper for streaming requests
async function* streamRequest<T>(
  options: StreamRequestOptions,
): AsyncGenerator<T, void, unknown> {
  const token = await getAuthToken();
  const fullUrl = `${BASE_URL}${options.url}`;
  const finalRequestId = options.requestId || crypto.randomUUID();
  const streamQueue = createStreamQueue<T>();

  fetchEventSource(fullUrl, {
    method: options.method,
    headers: buildStreamHeaders(options, token, finalRequestId),
    body:
      options.body instanceof FormData
        ? options.body
        : JSON.stringify(options.body),
    openWhenHidden: true,
    async onopen(response) {
      if (!response.ok) {
        throw await toStreamOpenError(response, options.url);
      }
    },
    onmessage(msg) {
      processStreamMessage(
        msg,
        streamQueue.push,
        streamQueue.setDone,
        streamQueue.setError,
      );
    },
    onclose() {
      streamQueue.setDone();
    },
    onerror(err) {
      streamQueue.setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    },
  }).catch((err) => {
    streamQueue.setError(err instanceof Error ? err : new Error(String(err)));
  });

  while (!streamQueue.isDone() || streamQueue.queue.length > 0) {
    if (streamQueue.queue.length > 0) {
      const item = streamQueue.queue.shift();
      if (item !== undefined) yield item;
    } else if (streamQueue.getError()) {
      throw streamQueue.getError();
    } else {
      await streamQueue.wait();
    }
  }

  if (streamQueue.getError()) throw streamQueue.getError();
}

export const api = {
  // Image generation endpoints
  generation: {
    generateImageStream: (formData: FormData, requestId?: string) => {
      return streamRequest<HydratedImage>({
        url: "/api/generation/generate",
        method: "POST",
        body: formData,
        headers: {},
        requestId,
      });
    },

    regenerateWithAttributesStream: (
      id: string,
      data: RegenerateWithAttributesRequest,
      requestId?: string,
    ) =>
      streamRequest<RefinementStreamEvent>({
        url: `/api/refinement/regenerate-with-attributes/${id}`,
        method: "POST",
        body: data as unknown as Record<string, unknown>,
        headers: {},
        requestId,
      }),

    regenerateWithPromptStream: (
      id: string,
      data: RegenerateWithPromptRequest,
      requestId?: string,
    ) =>
      streamRequest<RefinementStreamEvent>({
        url: `/api/refinement/regenerate-with-prompt/${id}`,
        method: "POST",
        body: data as unknown as Record<string, unknown>,
        headers: {},
        requestId,
      }),
  },

  // Region selection endpoints; only this streaming half is hand-written.
  selection: {
    createDesignStream: (imageId: string, data: SelectionCreateDesignRequest) =>
      streamRequest<HydratedImage>({
        url: `/api/selection/create-design/${imageId}`,
        method: "POST",
        body: data as unknown as Record<string, unknown>,
        headers: {},
      }),

    replaceAssetStream: (imageId: string, data: SelectionReplaceAssetRequest) =>
      streamRequest<HydratedImage>({
        url: `/api/selection/replace-asset/${imageId}`,
        method: "POST",
        body: data as unknown as Record<string, unknown>,
        headers: {},
      }),
  },

  // Image endpoints
  images: {
    download: (imageId: string) =>
      apiClient.get<Blob>(`/api/images/${imageId}/download`, {
        responseType: "blob",
      }),
    downloadUpscaled: (imageId: string, scale: 2 | 4) =>
      apiClient.get<Blob>(`/api/images/${imageId}/download/upscaled`, {
        params: { scale },
        responseType: "blob",
      }),
  },
};

// ============================================================================
// Error Handling Utilities
// ============================================================================

export function isApiError(
  error: unknown,
): error is AxiosError<{ detail: string }> {
  return (
    axios.isAxiosError(error) &&
    error.response?.data !== undefined &&
    typeof error.response.data === "object" &&
    error.response.data !== null &&
    "detail" in error.response.data
  );
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.detail || "An unknown error occurred";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unknown error occurred";
}

export default apiClient;
